/* ------------------------------------------------------------------
   Matching Set Builder — Phase 3 interactivity + Phase 4 cart
   integration + Fabric-parity Confirm/collapse UX pass + paid
   Customisation service integration.

   Scope: Relationship -> People -> Gender -> Size -> Style ->
   Confirm Person -> live pricing preview -> Add to Cart. Phase 3's
   relationship rules, person creation, and the missing-catalog-data
   safeguard remain unchanged; the Fabric-parity pass replaced
   Gender/Size/Style pill-button groups with native <select> controls
   (Relationship stays the builder's only chip/button layer) and added
   an explicit per-person Confirm/collapse lifecycle modeled on
   Fabric's already-approved Confirm Set lifecycle (`confirmed`/
   `expanded` state, confirmPerson()/expandPerson()). Overall cart
   readiness and `ProductConfigured` require every generated person to
   be explicitly CONFIRMED, not merely field-complete -- see
   computeProgress()/renderSummary() below. The live pricing preview
   deliberately keeps calculating from *complete* people (unchanged),
   not only confirmed ones, so the customer still sees a useful
   running estimate while configuring multiple people, before tapping
   Confirm on each -- a deliberate divergence from Fabric's pricing
   lifecycle (Implementation Approval, that phase).

   This file is a small, self-contained component bound only to
   [data-matching-builder] root(s). It does not touch global product
   forms, the native variant-picker, or any other section, and does
   not add anything to `window` of its own (it reads the shared
   window.PTCustomisation module -- see below -- but does not extend
   it). All state lives in a closure per builder instance created by
   init().

   Data source of truth: the JSON island rendered by
   snippets/matching-set-builder.liquid
   (#matching-builder-data-<section.id>) — real Shopify variant ids/
   selling prices and real Gender-metaobject Size lists, and
   Style Master entries from the shared PTStyleCatalog resolver.
   No style catalogue or style count is hardcoded here.

   CART ARCHITECTURE (Phase 4, locked): a Matching Set is never one
   combined SKU. Every configured person is submitted as its own
   cart line using their real Gender variant id, with public
   `Size`/`Style` properties and private `_set_id` (shared by every
   person in one submission) / `_relationship` / `_person` (unique per
   person — reuses the existing per-instance-unique `person.id`)
   properties. Submission is a single multi-item POST to
   `theme.routes.cartAdd` (`/cart/add.js`) — never one request per
   person. `_set_id`/`_relationship`/`_person` are hidden from the
   customer-facing cart entirely by the theme's own existing
   underscore-property filter in snippets/cart-item.liquid; nothing
   here needs to replicate that.

   PRICING: Subtotal, Matching Benefit and estimated garment total are
   PDP-only previews. Shopify automatic discounts remain authoritative.

   CTA note: the "Add to Cart" button (`[data-matching-add-to-cart]`)
   is a dedicated, always type="button" control, not part of any
   native product form (this template renders no `buy-buttons`
   block), so it never needs to intercept or prevent a native
   submission. It stays natively `disabled` until every generated
   person is explicitly CONFIRMED, and again while a submission is in
   flight, driven entirely through the existing render() pipeline.

   PAID CUSTOMISATION SERVICE (this phase):
   - A person can now also be "Customised" instead of Standard --
     `person.customisation` (a draft object owned by, and validated
     by, the shared popup module assets/pt-customisation-popup.js) is
     truthy exactly when it is. See isCustomised()/buildPerson().
   - The legacy Custom-as-Size path (CUSTOM_SIZE_VALUE, setPersonSize()'s
     Custom-boundary branch, the disabled [data-matching-custom-note]
     CTA) has been removed now that this real Customisation service is
     live. Custom Size replaces standard size completion only; Style
     remains required through the regular builder control.
   - Saving Custom Size preserves standard size for reversal and the
     regular Style Code for both sizing paths. Style remains editable.
   - Per explicit product decision, Matching does NOT add friction to
     an ordinary Standard Gender change: the inline Gender-change
     confirmation (requestGenderChange()/pendingGenderChange) is shown
     ONLY when the person has a saved Customisation to lose --
     unlike Fabric, which also warns on Standard Size/Style loss.
     Gender changes clear Style because Style Codes are gender-specific.
   - handleAddToCart() submits one extra cart line per Customised
     person (the real Shopify product configured in Theme Settings --
     see PTCustomisation.getProductInfo() -- never a client-invented
     price/variant), identified by its own private `_customisation_id`
     (never `_set_id`/`_person`/`_relationship`), generated fresh at
     submit time so two Customised people never accidentally merge
     into one cart line, and so a Customisation product line is never
     mistaken for part of the `_set_id` garment group (see
     snippets/cart-matching-group.liquid's grouping logic).
   - The garment benefit excludes paid customisation. The
     ₹500 Customisation charge appears where every other service/add-on
     product already does -- as its own line in the real Shopify cart
     -- included separately at full price in the preview summary.
   - The shared popup module (window.PTCustomisation) is rendered once
     by snippets/matching-set-builder.liquid and is completely
     builder-agnostic; this file owns only the two state transitions
     (saveCustomisation()/useStandardOptions()) and Gender-change
     invalidation, exactly per its reusability boundary.
------------------------------------------------------------------- */
(function () {
  'use strict';

  // Note: the legacy CUSTOM_SIZE_VALUE routing constant has been
  // removed along with Custom-as-Size -- snippets/matching-set-
  // builder.liquid's *_sizes_json captures now exclude the
  // metaobject's CUSTOM entry entirely, so the Matching Size <select>
  // never offers a "Custom" value. The paid Customisation service
  // (window.PTCustomisation) is the sole "something different" path
  // now.

  var GENDER_TEXT = {
    WOMEN: 'Woman',
    MEN: 'Man',
    GIRLS: 'Girl',
    BOYS: 'Boy'
  };

  // Frozen relationship rules (see MATCHING-SET-BUILDER-SPEC.md).
  // Unchanged from the previous Phase 3 pass.
  var RELATIONSHIP_DEFS = {
    couple: {
      groups: [
        { key: 'couple', min: 2, max: 2, fixedGenders: ['WOMEN', 'MEN'], labels: ['Woman', 'Man'] }
      ]
    },
    parentkids: {
      groups: [
        { key: 'adult', min: 1, max: 2, genderChoices: ['WOMEN', 'MEN'], labelPrefix: 'Parent' },
        { key: 'kid', min: 1, max: 3, genderChoices: ['GIRLS', 'BOYS'], labelPrefix: 'Kid' }
      ]
    },
    kids: {
      groups: [
        { key: 'kid', min: 2, max: 4, genderChoices: ['GIRLS', 'BOYS'], labelPrefix: 'Kid' }
      ]
    },
    family: {
      groups: [
        { key: 'person', min: 2, max: 8, genderChoices: ['WOMEN', 'MEN', 'GIRLS', 'BOYS'], labelPrefix: 'Person' }
      ]
    }
  };

  /* ---- Shopify-standard money formatting, reimplemented locally ----
     No global `Shopify.formatMoney` helper is exposed by this theme,
     so this is a small local utility (not a global) that applies the
     shop's own `money_format` string (passed through the data island
     as `moneyFormat`) to a cents value, using the same placeholder
     syntax Shopify themes use ({{amount}}, {{amount_no_decimals}},
     {{amount_with_comma_separator}}, etc). No currency symbol or
     separator is hardcoded here. Unchanged from the previous pass.
  */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
    format = format || '{{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'number' ? precision : 2;
      thousands = thousands || ',';
      decimal = decimal || '.';
      var value = (number / 100).toFixed(precision);
      var parts = value.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var fraction = parts[1] ? decimal + parts[1] : '';
      return dollars + fraction;
    }

    var placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholderKey = placeholderMatch ? placeholderMatch[1] : 'amount';
    var formatted;

    switch (placeholderKey) {
      case 'amount_no_decimals':
        formatted = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        formatted = withDelimiters(cents, 2);
    }

    return placeholderMatch ? format.replace(placeholderMatch[0], formatted) : formatted;
  }

  function init(root) {
    if (root.dataset.matchingInitialized === 'true') return;
    root.dataset.matchingInitialized = 'true';

    var instanceId = root.getAttribute('data-instance-id') || '';
    var dataScript = document.getElementById('matching-builder-data-' + instanceId);
    if (!dataScript) return;

    var data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (err) {
      return;
    }

    var els = {
      catalogError: root.querySelector('[data-matching-catalog-error]'),
      // Task K1: [data-matching-people-heading]/[data-matching-people-subtext]
      // ("Your Matching Set" / "Choose the details for each person.")
      // were removed from snippets/matching-set-builder.liquid (Change 2)
      // as redundant hierarchy -- there is nothing left to look up here.
      peopleContainer: root.querySelector('[data-matching-people]'),
      personTemplate: root.querySelector('[data-matching-person-template]'),
      summary: root.querySelector('[data-matching-summary]'),
      summaryMessage: root.querySelector('[data-matching-summary-message]'),
      summaryCount: root.querySelector('[data-matching-summary-count]'),
      pricing: root.querySelector('[data-matching-pricing]'),
      subtotal: root.querySelector('[data-matching-subtotal]'),
      matchingBenefitRow: root.querySelector('[data-matching-matching-benefit-row]'),
      matchingBenefit: root.querySelector('[data-matching-matching-benefit]'),
      benefitLabel: root.querySelector('[data-matching-benefit-label]'),
      finalPrice: root.querySelector('[data-matching-final-price]'),
      pricingDetail: root.querySelector('[data-matching-pricing-detail]'),
      totalLabel: root.querySelector('[data-matching-total-label]'),
      previewOriginal: root.querySelector('[data-matching-preview-original]'),
      previewNote: root.querySelector('[data-matching-preview-note]'),
      cartError: root.querySelector('[data-matching-cart-error]'),
      cta: root.querySelector('[data-matching-add-to-cart]')
    };

    if (!els.peopleContainer || !els.personTemplate) return;

    var personCounter = 0;
    var state = {
      relationshipKey: null,
      groups: [],
      people: [],
      submitting: false
    };

    var editMode = new URLSearchParams(window.location.search).has('edit_set');
    var editSession = null;
    var editBlocked = editMode;

    // Meta Pixel funnel events (ProductEngaged / ProductConfigured).
    // See assets/pdp-funnel-tracking.js for the shared fbq wrapper.
    // Local to this init() call -- resets per page load, never
    // persists across navigations. Additive observers only: never
    // touch PDP state, validation, the cart, or the Add to Cart
    // button.
    var funnelEngagedFired = false;
    var funnelConfiguredFired = false;

    function funnelTrackingParams(extra) {
      var params = {
        content_id: data.productId || undefined,
        content_name: data.productTitle || undefined,
        product_type: data.productType || undefined,
        currency: data.currencyCode || undefined
      };
      if (extra) {
        for (var key in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, key)) params[key] = extra[key];
        }
      }
      return params;
    }

    /* ---------------------------------------------------------------
       Frozen data/state logic — unchanged from the prior Phase 3
       pass. Relationship rules, Gender/Size sourcing, pricing, and
       the missing-catalog-data safeguard all live here and are not
       touched by this UI-only refinement.
    ------------------------------------------------------------------ */

    function nextPersonId() {
      personCounter += 1;
      return 'matching-person-' + instanceId + '-' + personCounter;
    }

    function genderAvailable(gender) {
      var entry = data.genderVariants && data.genderVariants[gender];
      return !!(entry && entry.available);
    }

    function sizesForGender(gender) {
      var entry = data.sizesByGender && data.sizesByGender[gender];
      var chartSizes = window.PTSizeGuide && window.PTSizeGuide.standardSizes(gender);
      if (chartSizes) return chartSizes;
      if (!entry || !entry.available || !entry.sizes || !entry.sizes.length) return null;
      return entry.sizes;
    }

    function priceForGender(gender) {
      var entry = data.genderVariants && data.genderVariants[gender];
      if (!entry || !entry.available) return null;
      return entry.priceCents;
    }

    function variantIdForGender(gender) {
      var entry = data.genderVariants && data.genderVariants[gender];
      if (!entry || !entry.available) return null;
      return entry.variantId;
    }

    function groupDefByKey(key) {
      for (var i = 0; i < state.groups.length; i++) {
        if (state.groups[i].key === key) return state.groups[i];
      }
      return null;
    }

    function buildPerson(groupDef, indexInGroup) {
      var person = {
        id: nextPersonId(),
        groupKey: groupDef.key,
        indexInGroup: indexInGroup,
        gender: null,
        fixedGender: null,
        size: null,
        style: null,
        // Fabric-parity Confirm/collapse state (mirrors
        // assets/fabric-product-options.js's buildSet() exactly): a
        // newly generated person starts open and unconfirmed.
        confirmed: false,
        expanded: true,
        // Gender-change confirmation (only ever set for a
        // selectable-gender person with a saved Customisation to
        // lose -- see requestGenderChange()).
        pendingGenderChange: null,
        // Paid Customisation service (assets/pt-customisation-popup.js).
        // `customisation` is the saved draft (see that file's
        // currentDraft() for its shape) once the customer has
        // explicitly clicked "CUSTOMISE THIS SET" -- its mere presence
        // is what makes a person "Customised" instead of "Standard";
        // see isCustomised()/personCompletionInfo(). Standard
        // `size`/`style` are deliberately NOT cleared when this is set
        // (see saveCustomisation()) so "Use standard options instead"
        // can restore them exactly. `customisationDraftCarryover` holds
        // the Gender-independent fields (name/measurements/notes)
        // preserved across a Gender change that invalidated an active
        // Customisation -- see invalidateCustomisationForGenderChange().
        customisation: null,
        customisationDraftCarryover: null
      };
      if (groupDef.fixedGenders) {
        person.fixedGender = groupDef.fixedGenders[indexInGroup] || null;
        person.gender = person.fixedGender;
      }
      return person;
    }

    function personLabel(person) {
      var groupDef = groupDefByKey(person.groupKey);
      if (!groupDef) return 'Person';
      if (groupDef.labels) {
        return groupDef.labels[person.indexInGroup] || GENDER_TEXT[person.fixedGender] || 'Person';
      }
      return (groupDef.labelPrefix || 'Person') + ' ' + (person.indexInGroup + 1);
    }

    function lastIndexOfGroup(key) {
      for (var i = state.people.length - 1; i >= 0; i--) {
        if (state.people[i].groupKey === key) return i;
      }
      return -1;
    }

    function rebuildPeopleForGroup(groupDef) {
      var existingCount = 0;
      for (var i = 0; i < state.people.length; i++) {
        if (state.people[i].groupKey === groupDef.key) existingCount++;
      }
      if (existingCount < groupDef.count) {
        for (var add = existingCount; add < groupDef.count; add++) {
          state.people.push(buildPerson(groupDef, add));
        }
      } else if (existingCount > groupDef.count) {
        var toRemove = existingCount - groupDef.count;
        for (var r = 0; r < toRemove; r++) {
          var idx = lastIndexOfGroup(groupDef.key);
          if (idx > -1) state.people.splice(idx, 1);
        }
      }
    }

    function selectRelationship(key, options) {
      var def = RELATIONSHIP_DEFS[key];
      if (!def) return;

      state.relationshipKey = key;
      state.people = [];
      personCounter = 0;

      // `silent` marks a SYSTEM-driven selection (automatic relationship
      // init from product data on page load) as distinct from genuine
      // customer engagement -- it suppresses only this ProductEngaged
      // attempt. The real click handler below calls
      // selectRelationship(target.value) with no second argument, so
      // `silent` is always false/undefined there and this block runs
      // exactly as before for every real customer interaction.
      var silent = !!(options && options.silent);
      if (!silent && !funnelEngagedFired && window.PTFunnelTracking) {
        var funnelEngagedAccepted = window.PTFunnelTracking.fire('ProductEngaged', funnelTrackingParams({
          selected_relationship: key
        }));
        // Only latch the guard once the helper has actually taken
        // ownership of the event (sent, or safely queued) -- never
        // on a discard, so a later real interaction gets a fair
        // retry.
        if (funnelEngagedAccepted) funnelEngagedFired = true;
      }

      state.groups = def.groups.map(function (g) {
        return {
          key: g.key,
          min: g.min,
          max: g.max,
          count: g.min,
          fixedGenders: g.fixedGenders || null,
          labels: g.labels || null,
          genderChoices: g.genderChoices || null,
          labelPrefix: g.labelPrefix || null
        };
      });

      state.groups.forEach(function (groupDef) {
        for (var i = 0; i < groupDef.count; i++) {
          state.people.push(buildPerson(groupDef, i));
        }
      });

      render();
    }

    function changeGroupCount(key, delta) {
      var groupDef = groupDefByKey(key);
      if (!groupDef) return;
      var next = groupDef.count + delta;
      if (next < groupDef.min || next > groupDef.max) return;
      groupDef.count = next;
      rebuildPeopleForGroup(groupDef);
      // A newly added person already starts expanded (buildPerson()'s
      // default) and every other person's confirmed/expanded state is
      // left untouched -- already-confirmed people stay collapsed,
      // still-open incomplete people stay open, per this phase's
      // spec. Only a gentle scroll-into-view nudge is added here, for
      // the added-person case, mirroring Fabric's identical courtesy.
      var addedIndex = delta > 0 ? lastIndexOfGroup(key) : -1;
      var addedPersonId = addedIndex > -1 ? state.people[addedIndex].id : null;
      render();
      if (addedPersonId) scrollPersonIntoView(addedPersonId);
    }

    function findPerson(id) {
      for (var i = 0; i < state.people.length; i++) {
        if (state.people[i].id === id) return state.people[i];
      }
      return null;
    }

    // Applies a Gender change. Called directly from requestGenderChange()
    // when there is no saved Customisation to lose, or from
    // confirmGenderChange() once the customer has explicitly confirmed
    // losing one.
    function setPersonGender(personId, gender) {
      var person = findPerson(personId);
      if (!person || person.fixedGender) return;
      if (person.gender === gender) return;
      person.gender = gender;
      // Size and Style Master identities are gender-specific.
      person.size = null;
      person.style = null;
      // A saved Customisation IS Gender-scoped (its Top Size/
      // Bottom Size values come from that Gender's own catalogue --
      // assets/pt-customisation-popup.js), so it must not silently
      // remain "valid" under a different Gender -- see
      // invalidateCustomisationForGenderChange() below. Safe no-op
      // whenever there is no saved Customisation, so this adds no
      // friction to an ordinary Standard Gender change.
      invalidateCustomisationForGenderChange(person);
      if (person.confirmed) person.confirmed = false;
      render();
    }

    // Gender changes on a person with a saved Customisation are routed
    // through a lightweight, non-native confirmation instead of
    // applying immediately -- see the file header: per explicit
    // product decision, Matching does NOT add this friction for an
    // ordinary Standard Gender change (no saved Customisation), unlike
    // Fabric, which also warns on Standard Size/Style loss. A Gender
    // chosen on a person with no saved Customisation applies
    // immediately, exactly as before this phase.
    function requestGenderChange(personId, gender) {
      var person = findPerson(personId);
      if (!person || person.fixedGender) return;
      if (person.gender === gender) return;
      if (person.customisation) {
        person.pendingGenderChange = gender;
        render();
        return;
      }
      setPersonGender(personId, gender);
    }

    function confirmGenderChange(personId) {
      var person = findPerson(personId);
      if (!person || !person.pendingGenderChange) return;
      var gender = person.pendingGenderChange;
      person.pendingGenderChange = null;
      setPersonGender(personId, gender);
    }

    function cancelGenderChange(personId) {
      var person = findPerson(personId);
      if (!person) return;
      person.pendingGenderChange = null;
      render();
    }

    // Every Size value is now a real, standard one (legacy Custom-as-
    // Size has been removed) -- a Size change never needs to clear
    // Style. A real change to Size still clears `confirmed`. Mirrors
    // Fabric's identical setSize() rule.
    function setPersonSize(personId, size) {
      var person = findPerson(personId);
      if (!person) return;
      var next = size || null;
      if (person.size === next) return;
      person.size = next;
      if (person.confirmed) person.confirmed = false;
      render();
    }

    function setPersonStyle(personId, style) {
      var person = findPerson(personId);
      if (!person) return;
      var next = window.PTStyleCatalog.find(person.gender, style) ? style : null;
      if (person.style === next) return;
      person.style = next;
      if (person.confirmed) person.confirmed = false;
      render();
    }

    // Confirm/collapse lifecycle (mirrors Fabric's expandSet()/
    // confirmSet() exactly, simplified: Matching does not need
    // Fabric's multi-set accordion cap or two-tap armed-remove
    // guard, neither of which this task's spec calls for -- keeping
    // this to the minimum that satisfies "new person opens expanded,
    // already-confirmed people stay collapsed, Edit reopens, tapping
    // an already-expanded confirmed header re-collapses it").
    function expandPerson(personId) {
      var person = findPerson(personId);
      if (!person) return;
      person.expanded = true;
      render();
    }

    function confirmPerson(personId) {
      var person = findPerson(personId);
      if (!person) return;
      var info = personCompletionInfo(person);
      if (!info.complete) return;
      person.confirmed = true;
      person.expanded = false;
      render();
    }

    /* ---------------------------------------------------------------
       Paid Customisation service -- assets/pt-customisation-popup.js
       owns the popup itself (form fields, validation, the Gender-
       specific Top/Bottom Size catalogue); this file owns only
       the two transitions below, exactly per the reusability boundary.
       Opening the popup (see onClick()'s [data-matching-customise-cta]
       branch) never calls either of these -- only an explicit save
       from inside the popup (onSave) or an explicit "Use standard
       options instead" click does.
    ------------------------------------------------------------------ */
    // Called from PTCustomisation's onSave callback once the customer
    // has explicitly clicked "CUSTOMISE THIS SET". Standard size/style
    // are deliberately left untouched (see the file header and
    // buildPerson()) so a later useStandardOptions() call restores
    // them exactly as they were. This never sets `confirmed` --
    // explicit Confirm Person remains mandatory regardless of
    // Customisation.
    function saveCustomisation(personId, draft) {
      var person = findPerson(personId);
      if (!person) return;
      person.customisation = draft;
      person.customisationDraftCarryover = null;
      if (person.confirmed) person.confirmed = false;
      render();
    }

    // "Use standard options instead" (Customisation reversal). Only
    // clears `customisation`, restoring the retained standard size while
    // keeping the current regular Style selection.
    function useStandardOptions(personId) {
      var person = findPerson(personId);
      if (!person) return;
      if (!person.customisation) return;
      person.customisation = null;
      if (person.confirmed) person.confirmed = false;
      render();
    }

    // Called from setPersonGender() whenever a Gender change actually
    // applies. A saved Customisation is Gender-scoped (its Top/Bottom Size
    // values come from that Gender's own catalogue -- assets/pt-
    // customisation-popup.js), so it must not silently remain "valid"
    // under a different Gender. Gender-independent fields (name,
    // measurements, notes) are worth keeping -- stashed as
    // `customisationDraftCarryover` so they resurface next time the
    // popup opens for this person -- but Top Size/Bottom Size
    // are dropped, and the person stops counting as Customised until
    // the customer explicitly reviews and re-saves it (see
    // personCompletionInfo()). Mirrors Fabric's identical
    // invalidateCustomisationForGenderChange() exactly.
    function invalidateCustomisationForGenderChange(person) {
      if (!person.customisation) return;
      var prev = person.customisation;
      person.customisationDraftCarryover = {
        profileName: prev.profileName,
        chest: prev.chest,
        waist: prev.waist,
        hips: prev.hips,
        height: prev.height,
        notes: prev.notes,
        measurementsLater: prev.measurementsLater
      };
      person.customisation = null;
    }

    // Draft to pre-fill when opening the popup for this person: the
    // saved Customisation if one exists (editing), otherwise a
    // Gender-change carryover if one exists (see above), otherwise
    // null. Deliberately Matching-label-agnostic (the shared popup
    // must not be taught about Matching labels) -- the Profile Name
    // default-to-person-label behaviour is layered on by the caller,
    // at the point of opening the popup (see onClick() below).
    function draftForPopup(person) {
      return person.customisation || person.customisationDraftCarryover || null;
    }

    // Whether a person is currently in Customised mode. Deliberately a
    // one-line predicate (not inlined at each call site) so every
    // place that branches on Standard-vs-Customised reads the same
    // way -- mirrors Fabric's identical isCustomised().
    function isCustomised(person) {
      return !!person.customisation;
    }

    /* ---------------------------------------------------------------
       Completion / validation. Every active Standard person needs
       Gender + Size + Style; a Customised person needs only Gender + Style +
       an explicitly saved Customisation. "Complete" only gates whether
       a person *can* be confirmed -- cart-readiness and
       ProductConfigured are gated on `confirmed` (see
       computeProgress()/renderSummary() below), mirroring Fabric's
       identical complete-vs-confirmed split.
    ------------------------------------------------------------------ */
    function personCompletionInfo(person) {
      var gender = person.gender;
      var genderChosen = !!gender;
      var genderResolved = genderChosen && genderAvailable(gender);
      var blockedByCatalog = genderChosen && !genderAvailable(gender);

      var styleValid = !!window.PTStyleCatalog.find(gender, person.style);
      var stylesAvailable = window.PTStyleCatalog.options(gender).length > 0;
      blockedByCatalog = blockedByCatalog || (genderResolved && !stylesAvailable);
      // Custom Size replaces standard size only; the regular Style is required.
      if (isCustomised(person)) {
        var customisedMissing = genderResolved ? [] : ['Gender'];
        if (!styleValid) customisedMissing.push('Style');
        return { complete: genderResolved && styleValid, missing: customisedMissing, blockedByCatalog: blockedByCatalog };
      }

      var sizeList = genderResolved ? sizesForGender(gender) : null;
      var sizeDataOk = genderResolved && !!sizeList;
      var styleDataOk = genderResolved && window.PTStyleCatalog.options(gender).length > 0;
      blockedByCatalog = blockedByCatalog || (genderResolved && !sizeDataOk);

      var missing = [];
      if (!gender) missing.push('Gender');
      if (!person.size) missing.push('Size');
      if (!styleValid) missing.push('Style');

      var complete = genderResolved && sizeDataOk && !!person.size && styleDataOk && styleValid;

      return { complete: complete, missing: missing, blockedByCatalog: blockedByCatalog };
    }

    function computeProgress() {
      var total = state.people.length;
      var completeCount = 0;
      var confirmedCount = 0;
      var anyBlocked = false;
      var incompletePeople = [];
      var unconfirmedCompletePeople = [];

      state.people.forEach(function (person) {
        var info = personCompletionInfo(person);
        if (info.complete) {
          completeCount++;
          if (person.confirmed) {
            confirmedCount++;
          } else {
            unconfirmedCompletePeople.push({ person: person, info: info });
          }
        } else {
          incompletePeople.push({ person: person, info: info });
        }
        if (info.blockedByCatalog) anyBlocked = true;
      });

      return {
        total: total,
        completeCount: completeCount,
        confirmedCount: confirmedCount,
        incompletePeople: incompletePeople,
        unconfirmedCompletePeople: unconfirmedCompletePeople,
        anyBlocked: anyBlocked
      };
    }

    /* ---------------------------------------------------------------
       Pricing / savings preview. Only confirmed, complete people
       (Gender + Size + Style all chosen) are counted here -- they are
       the exact population that would actually be submitted to
       Shopify right now, so the previewed matching-benefit tier
       always matches what a real Add to Cart click would submit.
       Every amount is integer cents until the final formatMoney()
       call. This never inspects or recreates Shopify's own automatic
       discount -- see the file header comment.
    ------------------------------------------------------------------ */
    function computePricing() {
      var pricedPeople = state.people.filter(function (person) {
        return person.confirmed && personCompletionInfo(person).complete;
      });
      var count = pricedPeople.length;
      var subtotalCents = pricedPeople.reduce(function (total, person) {
        return total + (priceForGender(person.gender) || 0);
      }, 0);

      // PDP-only estimate. Existing cart items are not inferred; paid services are added after the garment benefit.
      var tierPercent = count >= 3 ? 15 : (count === 2 ? 10 : 0);
      var matchingBenefitCents = Math.round((subtotalCents * tierPercent) / 100);
      var serviceInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : null;
      var serviceCount = pricedPeople.filter(function (person) { return !!person.customisation; }).length;
      var serviceCents = serviceInfo && serviceInfo.available ? serviceCount * serviceInfo.priceCents : 0;
      return {
        count: count,
        subtotalCents: subtotalCents,
        serviceCents: serviceCents,
        tierPercent: tierPercent,
        matchingBenefitCents: matchingBenefitCents,
        finalPriceCents: subtotalCents - matchingBenefitCents + serviceCents
      };
    }

    // Mirrors Fabric's identical summaryMessageText() structure,
    // adapted for people/groups and Matching's own copy. Distinguishes
    // "still has missing fields" from "complete but not yet
    // confirmed" so the prompt tells the customer exactly what to do
    // next.
    function summaryMessageText(progress) {
      if (state.allComplete) return 'Your matching set is ready';
      if (progress.anyBlocked) return 'Some options are unavailable — review your selections to continue';
      if (!state.people.some(function (item) { return item.size || item.style || item.customisation; })) {
        return 'Select size & style for each person to continue';
      }
      if (progress.completeCount === progress.total) {
        return 'Confirm each person to continue';
      }
      var remaining = progress.total - progress.confirmedCount;
      return progress.confirmedCount + ' of ' + progress.total + ' people completed — finish ' + remaining + ' more to continue';
    }

    // Collapsed-card order-review line. The Gender segment lives on
    // the person's own heading (see personHeadingText() below), so
    // this line covers only Size and Style. Mirrors
    // Fabric's identical buildCollapsedSummaryText().
    function buildCollapsedSummaryText(person) {
      if (!person.gender) return 'Not started yet';
      if (isCustomised(person)) {
        var custPriceInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : null;
        return ((custPriceInfo && custPriceInfo.priceFormatted) ? ('Customised · ' + custPriceInfo.priceFormatted) : 'Customised')
          + '\n' + (window.PTStyleCatalog.label(person.gender, person.style) || 'Style not chosen');
      }
      if (!person.size) return 'Size not chosen';
      var head = window.PTSizeGuide ? window.PTSizeGuide.summary(person.gender, person.size) : person.size;
      head += '\n' + (person.style ? window.PTStyleCatalog.label(person.gender, person.style) : 'Style not chosen');
      return head;
    }

    // The card heading text. Expanded, it is always just the plain
    // role/identity label ("Parent 1", "Woman", "You"). Collapsed, a
    // selectable-Gender person additionally appends "· <Gender>"
    // (e.g. "Parent 1 · Woman"), matching Fabric's "Set 1 · Women"
    // pattern -- a fixed-Gender person (Couple) needs no suffix
    // because personLabel() already resolves directly to their
    // identity ("Woman"/"Man") via RELATIONSHIP_DEFS.couple's own
    // `labels`, so appending the same text again would be redundant.
    function personHeadingText(person, collapsed) {
      var base = personLabel(person);
      if (!collapsed || person.fixedGender || !person.gender) return base;
      return base + ' · ' + (GENDER_TEXT[person.gender] || person.gender);
    }

    /* ---------------------------------------------------------------
       Rendering — Gender/Size/Style now render as native <select>
       controls (populateSelect) instead of pill-button groups;
       Relationship above remains the builder's only chip/button
       layer. Each group's "+/-" stepper is replaced by an explicit
       "+ Add Another <Role>" action, and an optional/added person
       carries its own "Remove" action instead of a shared counter.
       Confirmed people collapse to a compact "Label · Gender ✓" /
       "Size · Style" order-review card with an Edit affordance,
       mirroring Fabric's identical set-card lifecycle.
    ------------------------------------------------------------------ */

    function personNoun(groupDef) {
      if (groupDef.key === 'adult') return 'Parent';
      if (groupDef.key === 'kid') return 'Kid';
      return 'Person';
    }

    // Task K1.1: groupTitleText() ("Parents"/"Kids"/"People") removed
    // along with the group heading it used to label -- see
    // renderPeopleGroups()/attachGroupAddAction() below. No remaining
    // caller.

    // Populates a native <select> for Gender, Size, or Style. A
    // native select needs no per-option wrapper markup and already
    // provides its own keyboard/touch affordances, so this replaces
    // the previous buildPillGroup() pill-button renderer entirely --
    // mirrors assets/fabric-product-options.js's identical
    // populateSelect() exactly. The leading placeholder option is
    // disabled/hidden and only ever shown when nothing is selected
    // yet -- it is never a real, selectable value.
    function populateSelect(selectEl, config) {
      selectEl.innerHTML = '';
      var anyEnabled = false;

      var placeholderOpt = document.createElement('option');
      placeholderOpt.value = '';
      placeholderOpt.textContent = config.placeholder;
      placeholderOpt.disabled = true;
      placeholderOpt.hidden = true;
      selectEl.appendChild(placeholderOpt);

      var hasSelection = false;
      config.options.forEach(function (opt) {
        if (!opt.disabled) anyEnabled = true;
        var optionEl = document.createElement('option');
        optionEl.value = opt.value;
        optionEl.textContent = opt.text + (opt.disabled ? ' (unavailable)' : '');
        optionEl.disabled = !!opt.disabled;
        if (opt.selected) {
          optionEl.selected = true;
          hasSelection = true;
        }
        selectEl.appendChild(optionEl);
      });

      placeholderOpt.selected = !hasSelection;
      selectEl.disabled = !anyEnabled;
      return anyEnabled;
    }

    function buildPersonCard(person, groupDef, isRemovable) {
      var node = els.personTemplate.content.firstElementChild.cloneNode(true);
      node.setAttribute('data-person-id', person.id);
      ['size', 'style'].forEach(function (field) {
        var select = node.querySelector('[data-matching-' + field + '-select]');
        var label = node.querySelector('[data-pt-field-label="' + field + '"]');
        if (select && label) {
          select.id = person.id + '-' + field;
          label.htmlFor = select.id;
        }
      });
      var styleGuide = node.querySelector('[data-pt-style-guide]');
      if (styleGuide) {
        styleGuide.dataset.ptStyleGuide = person.gender || '';
        styleGuide.hidden = !person.gender;
      }
      var sizeGuide = node.querySelector('[data-pt-size-guide]');
      if (sizeGuide) {
        sizeGuide.dataset.ptSizeGuide = person.gender || '';
        sizeGuide.hidden = !person.gender;
      }

      var info = personCompletionInfo(person);
      node.classList.toggle('matching-builder__person--complete', info.complete);
      node.classList.toggle('matching-builder__person--confirmed', !!person.confirmed);
      node.classList.toggle('matching-builder__person--collapsed', !person.expanded);

      var labelEl = node.querySelector('[data-matching-person-label]');
      if (labelEl) labelEl.textContent = personHeadingText(person, !person.expanded);

      var errorEl = node.querySelector('[data-matching-person-error]');
      if (errorEl) {
        if (info.blockedByCatalog) {
          errorEl.textContent = 'Unavailable';
          errorEl.hidden = false;
        } else if (!info.complete && person.editNotice) {
          errorEl.textContent = person.editNotice;
          errorEl.hidden = false;
        } else {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
      }

      // Collapsed-only order-review line + Edit/Continue affordance
      // (mirrors Fabric's identical [data-fabric-set-summary-line] /
      // [data-fabric-set-edit-hint] toggling exactly).
      var summaryLineEl = node.querySelector('[data-matching-person-summary-line]');
      var editHintEl = node.querySelector('[data-matching-person-edit-hint]');
      if (!person.expanded) {
        if (summaryLineEl) {
          summaryLineEl.hidden = false;
          summaryLineEl.textContent = buildCollapsedSummaryText(person);
        }
        if (editHintEl) {
          editHintEl.hidden = false;
          editHintEl.textContent = person.confirmed ? 'Edit' : 'Continue';
        }
      } else {
        if (summaryLineEl) { summaryLineEl.hidden = true; summaryLineEl.textContent = ''; }
        if (editHintEl) editHintEl.hidden = true;
      }

      var bodyEl = node.querySelector('[data-matching-person-body]');
      if (bodyEl) bodyEl.hidden = !person.expanded;

      var removeBtn = node.querySelector('[data-matching-person-remove]');
      if (removeBtn) {
        removeBtn.hidden = !isRemovable;
        if (isRemovable) {
          removeBtn.setAttribute('data-matching-remove-group', person.groupKey);
          removeBtn.setAttribute('aria-label', 'Remove ' + personLabel(person));
        }
      }

      var genderField = node.querySelector('[data-matching-field-gender]');
      var genderSelect = node.querySelector('[data-matching-gender-select]');
      if (person.fixedGender) {
        if (genderField) genderField.hidden = true;
      } else if (genderField && genderSelect) {
        genderField.hidden = false;
        genderSelect.setAttribute('data-person-id', person.id);
        populateSelect(genderSelect, {
          placeholder: 'Choose a Gender',
          options: (groupDef.genderChoices || []).map(function (g) {
            return {
              value: g,
              text: GENDER_TEXT[g],
              selected: person.gender === g,
              disabled: !genderAvailable(g)
            };
          })
        });
      }

      // Gender-change confirmation -- only ever shown for a
      // selectable-gender person with a saved Customisation to lose
      // (see requestGenderChange()/pendingGenderChange). A fixed-
      // gender person (Couple) never has a Gender <select> to trigger
      // this from, so this block simply never becomes visible there.
      var genderConfirmEl = node.querySelector('[data-matching-gender-confirm]');
      if (genderConfirmEl) {
        if (person.pendingGenderChange) {
          genderConfirmEl.hidden = false;
          var genderConfirmTextEl = genderConfirmEl.querySelector('[data-matching-gender-confirm-text]');
          if (genderConfirmTextEl) {
            genderConfirmTextEl.textContent = 'Change gender? Your saved customisation sizes and style will need to be selected again. Measurements and notes will be kept.';
          }
        } else {
          genderConfirmEl.hidden = true;
        }
      }

      var sizeSelect = node.querySelector('[data-matching-size-select]');
      if (sizeSelect) {
        sizeSelect.setAttribute('data-person-id', person.id);
        var sizes = person.gender ? sizesForGender(person.gender) : null;
        if (sizes) {
          populateSelect(sizeSelect, {
            placeholder: 'Choose a Size',
            options: sizes.map(function (s) {
              return { value: s, text: window.PTSizeGuide ? window.PTSizeGuide.optionLabel(person.gender, s) : s, selected: person.size === s };
            })
          });
        } else {
          populateSelect(sizeSelect, {
            placeholder: person.gender ? 'Size data unavailable' : 'Gender first',
            options: []
          });
        }
      }

      // Custom Size hides only standard size; Style stays authoritative.
      var personIsCustomised = isCustomised(person);
      var selectRowEl = node.querySelector('[data-matching-select-row]');
      var styleSelect = node.querySelector('[data-matching-style-select]');

      var sizeFieldEl = node.querySelector('[data-matching-size-field]');
      if (sizeFieldEl) sizeFieldEl.hidden = personIsCustomised;
      if (window.PTSizeGuide) window.PTSizeGuide.snapshot(selectRowEl, person.gender, person.size, personIsCustomised);

      if (styleSelect) {
        styleSelect.setAttribute('data-person-id', person.id);
        if (person.gender) {
          populateSelect(styleSelect, {
            placeholder: 'Choose a Style',
            options: window.PTStyleCatalog.options(person.gender).map(function (s) {
              return { value: s.code, text: s.name, selected: person.style === s.code };
            })
          });
        } else {
          populateSelect(styleSelect, { placeholder: 'Select', options: [] });
        }
        if (window.PTStyleDropdown) window.PTStyleDropdown.enhance(styleSelect, person.gender);
      }

      /* -------------------------------------------------------------
         Customisation CTA / Customised panel (assets/pt-customisation-
         popup.js). The row is hidden entirely unless the popup module
         has loaded, the paid product is configured+available in Theme
         Settings, and this person's Gender has real catalogue data --
         never show an entry point that would silently fail to open.
         See PTCustomisation.getProductInfo()/isGenderReady().
      -------------------------------------------------------------- */
      var customiseRowEl = node.querySelector('[data-matching-customise-row]');
      var customiseCtaEl = node.querySelector('[data-matching-customise-row] > [data-matching-customise-cta]');
      var customiseCtaPriceEl = customiseCtaEl ? customiseCtaEl.querySelector('[data-matching-customise-cta-price]') : null;
      var customisedPanelEl = node.querySelector('[data-matching-customised-panel]');
      var customisedDetailEl = node.querySelector('[data-matching-customised-detail]');

      var custInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : { available: false };
      var custGenderReady = !!(person.gender && window.PTCustomisation && window.PTCustomisation.isGenderReady(person.gender));
      var custAvailable = !!(custInfo && custInfo.available) && custGenderReady;

      if (customiseRowEl) customiseRowEl.hidden = !custAvailable;
      if (customiseCtaEl) customiseCtaEl.hidden = personIsCustomised;
      if (customiseCtaPriceEl && custInfo.priceFormatted) {
        customiseCtaPriceEl.textContent = '+' + custInfo.priceFormatted;
      }
      if (customisedPanelEl) customisedPanelEl.hidden = !personIsCustomised;
      if (customisedDetailEl && personIsCustomised) {
        customisedDetailEl.textContent = custInfo.priceFormatted
          ? ('Custom size & fit · ' + custInfo.priceFormatted + ' per person')
          : 'Custom size & fit';
      }

      var confirmBtn = node.querySelector('[data-matching-person-confirm]');
      if (confirmBtn) {
        confirmBtn.disabled = !info.complete;
        confirmBtn.textContent = info.complete ? 'Confirm ' + GENDER_TEXT[person.gender] : 'Confirm Person';
      }
      var readyStatus = node.querySelector('[data-matching-confirm-ready]');
      if (readyStatus) readyStatus.hidden = !info.complete;

      return node;
    }

    function renderCatalogBanner(progress) {
      if (!els.catalogError) return;
      if (state.relationshipKey && progress.anyBlocked) {
        els.catalogError.textContent = 'Some sizes or genders needed for this set are not available in our catalog right now. Please contact us before ordering this configuration.';
        els.catalogError.hidden = false;
      } else {
        els.catalogError.hidden = true;
        els.catalogError.textContent = '';
      }
    }

    function renderPeopleGroups() {
      if (!state.relationshipKey) {
        els.peopleContainer.hidden = true;
        els.peopleContainer.innerHTML = '';
        return;
      }

      els.peopleContainer.hidden = false;
      els.peopleContainer.innerHTML = '';

      state.groups.forEach(function (groupDef) {
        var groupEl = document.createElement('div');
        groupEl.className = 'matching-builder__group';
        groupEl.setAttribute('data-matching-group', groupDef.key);

        // Task K1.1: the PARENTS/KIDS/PEOPLE group heading (and the
        // header-row that paired it with "+ Add <Role>") is gone --
        // redundant with each person's own label (Parent 1, Kid 1, ...).
        // The add action now attaches directly onto the FIRST person's
        // own header instead, see attachGroupAddAction() below.
        var canAdd = groupDef.min !== groupDef.max && groupDef.count < groupDef.max;

        var peopleWrap = document.createElement('div');
        peopleWrap.className = 'matching-builder__group-people';

        var groupPeople = state.people.filter(function (p) { return p.groupKey === groupDef.key; });
        groupPeople.forEach(function (person, idx) {
          // Only the most-recently-added person in an open-ended group
          // can be removed, mirroring the "+ Add" action that created
          // them one at a time -- required/base people (up to the
          // group's minimum) never show Remove. This also guarantees
          // idx 0 (the card attachGroupAddAction() below targets) never
          // shows Remove: every group here has min >= 1, so at idx 0
          // either count === min (isRemovable's `count > min` check
          // fails) or some later index is the true last one -- idx 0
          // can never satisfy both `count > min` and `idx === last`.
          // That is what makes it safe to reuse Remove's normally-empty
          // header slot for the Add action on this one card (see the
          // matching comment on .matching-builder__add-person in the
          // CSS, which relies on the same invariant).
          var isRemovable = groupDef.min !== groupDef.max
            && groupDef.count > groupDef.min
            && idx === groupPeople.length - 1;
          var personCardEl = buildPersonCard(person, groupDef, isRemovable);
          if (idx === 0 && canAdd) attachGroupAddAction(personCardEl, groupDef);
          peopleWrap.appendChild(personCardEl);
        });

        groupEl.appendChild(peopleWrap);

        els.peopleContainer.appendChild(groupEl);
      });
    }

    // Task K1.1: appends this group's "+ Add <Role>" action into the
    // FIRST person's own header row (same grid slot Remove would use
    // there, which is provably always empty -- see the isRemovable
    // comment above), so it reads as "Parent 1 ... + Add Parent"
    // rather than a separate group heading. Purely additive DOM
    // insertion onto an already-built card; does not touch
    // buildPersonCard() itself or any state it depends on.
    function attachGroupAddAction(personCardEl, groupDef) {
      var headerEl = personCardEl.querySelector('.matching-builder__person-header');
      if (!headerEl) return;
      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'matching-builder__add-person';
      addBtn.setAttribute('data-matching-add-person', groupDef.key);
      addBtn.textContent = '+ Add ' + personNoun(groupDef);
      headerEl.appendChild(addBtn);
    }

    // Render a garment-only estimate; zero-benefit rows remain hidden.
    function renderPricing(pricing) {
      if (!els.pricing) return;
      var show = !!state.relationshipKey && pricing.count > 0;
      els.pricing.hidden = !show;
      if (!show) return;

      // Conditional first-person example; qualification still requires two garments.
      var previewMatch = pricing.count === 1 && state.people.length >= 2;
      var displayTotal = previewMatch
        ? pricing.subtotalCents - Math.round(pricing.subtotalCents * 10 / 100) + pricing.serviceCents
        : pricing.finalPriceCents;
      els.pricing.classList.toggle('matching-builder__pricing--preview', previewMatch);
      if (els.pricingDetail) els.pricingDetail.hidden = previewMatch;
      if (els.totalLabel) els.totalLabel.textContent = previewMatch ? 'With your completed match' : 'Your set total';
      if (els.previewOriginal) {
        els.previewOriginal.hidden = !previewMatch && !pricing.tierPercent;
        els.previewOriginal.textContent = formatMoney(pricing.subtotalCents + pricing.serviceCents, data.moneyFormat);
      }
      if (els.previewNote) {
        els.previewNote.hidden = pricing.count >= 3;
        els.previewNote.textContent = previewMatch
          ? 'Complete the next person to unlock 10% Matching Benefit.'
          : (pricing.count === 1 ? 'Add another pajama set to unlock 10% Matching Benefit.' : '3+ pajama sets get 15% Matching Benefit.');
      }
      var serviceRow = root.querySelector('[data-matching-service-row]');
      if (serviceRow) {
        serviceRow.hidden = !pricing.serviceCents;
        serviceRow.querySelector('[data-matching-service-price]').textContent = '+' + formatMoney(pricing.serviceCents, data.moneyFormat);
      }
      if (els.subtotal) els.subtotal.textContent = formatMoney(pricing.subtotalCents, data.moneyFormat);
      if (els.finalPrice) els.finalPrice.textContent = formatMoney(displayTotal, data.moneyFormat);
      var showBenefit = pricing.matchingBenefitCents > 0;
      if (els.matchingBenefitRow) els.matchingBenefitRow.hidden = !showBenefit;
      if (els.matchingBenefit && showBenefit) {
        els.matchingBenefit.textContent = '−' + formatMoney(pricing.matchingBenefitCents, data.moneyFormat);
      }
      if (els.benefitLabel) els.benefitLabel.textContent = 'Matching Benefit — ' + pricing.tierPercent + '%';
    }

    function renderSummary(progress) {
      // Fabric-parity: the count line now reports how many people are
      // explicitly CONFIRMED, not merely field-complete -- mirrors
      // Fabric's identical "N of M sets confirmed" wording/gating.
      if (els.summaryCount) {
        els.summaryCount.textContent = state.relationshipKey
          ? (progress.confirmedCount + ' of ' + progress.total + ' people confirmed')
          : '0 people configured';
      }


      // Only confirmed garments contribute to the preview.
      renderPricing(computePricing());

      // Overall readiness (cart CTA + ProductConfigured) now requires
      // every generated person to be explicitly CONFIRMED -- a
      // complete-but-unconfirmed person no longer counts. `allComplete`
      // is kept as the state-field name (unchanged meaning-shift
      // pattern Fabric itself uses for its own equivalent flag) to
      // avoid an unnecessary rename elsewhere in this file.
      var allConfirmed = !!state.relationshipKey && progress.total > 0 && progress.confirmedCount === progress.total && !progress.anyBlocked;
      state.allComplete = allConfirmed;

      if (els.summaryMessage) {
        els.summaryMessage.textContent = summaryMessageText(progress);
      }

      if (allConfirmed && !funnelConfiguredFired && window.PTFunnelTracking) {
        var funnelPricing = computePricing();
        var funnelConfiguredAccepted = window.PTFunnelTracking.fire('ProductConfigured', funnelTrackingParams({
          selected_relationship: state.relationshipKey,
          set_count: progress.total,
          value: (typeof funnelPricing.finalPriceCents === 'number')
            ? (funnelPricing.finalPriceCents / 100)
            : undefined
        }));
        if (funnelConfiguredAccepted) funnelConfiguredFired = true;
      }

      if (els.summary) {
        els.summary.classList.toggle('matching-builder__summary--ready', allConfirmed);
      }

      // Disabled whenever every person isn't yet confirmed, and again
      // while a submission is in flight -- both states flow through
      // this one render() pass rather than being managed separately,
      // so there is exactly one place that decides whether the CTA
      // can be clicked.
      if (els.cta) {
        if (state.submitting) {
          els.cta.disabled = true;
          els.cta.setAttribute('aria-busy', 'true');
          els.cta.textContent = editMode ? 'Updating…' : 'Adding…';
        } else {
          els.cta.disabled = !allConfirmed || editBlocked;
          els.cta.removeAttribute('aria-busy');
          els.cta.textContent = editMode ? 'UPDATE CART' : 'Add to Cart';
        }
      }
    }

    function render() {
      var progress = computeProgress();
      renderCatalogBanner(progress);
      renderPeopleGroups();
      renderSummary(progress);
    }

    /* ---------------------------------------------------------------
       Phase 4 — Add to Cart submission.

       One multi-item POST to theme.routes.cartAdd (/cart/add.js);
       every configured person becomes one item using their real
       Gender variant id. _set_id is generated fresh for this single
       submission and shared by every item in it; _relationship is
       the selected relationship key; _person reuses the existing
       per-instance-unique person.id, which the Phase 4 assessment
       confirmed never repeats for the life of this builder instance
       (monotonic counter, not reset by Remove). Duplicate people
       (identical Gender/Size/Style) therefore still get distinct
       properties objects, which is what keeps Shopify from merging
       their cart lines into one.
    ------------------------------------------------------------------ */

    function generateSetId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      // Lightweight fallback for browsers without crypto.randomUUID:
      // timestamp + two random base36 chunks. Not cryptographic, but
      // only needs to be unique enough to group one submission's
      // cart lines together -- never used as a security token.
      var randomPart = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
      return 'set-' + Date.now().toString(36) + '-' + randomPart;
    }

    function setCartError(message) {
      if (!els.cartError) return;
      if (message) {
        els.cartError.textContent = message;
        els.cartError.hidden = false;
      } else {
        els.cartError.textContent = '';
        els.cartError.hidden = true;
      }
    }

    // Gentle scroll-into-view nudge for a newly added or just-expanded
    // person -- mirrors Fabric's identical scrollSetIntoView(). "nearest"
    // only scrolls when the card isn't already sufficiently in view.
    function scrollPersonIntoView(personId) {
      var node = els.peopleContainer.querySelector('[data-person-id="' + personId + '"]');
      if (!node) return;
      if (typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    function handleAddToCart() {
      if (state.submitting || editBlocked) return;

      // Re-validate at click time rather than trusting the disabled
      // attribute alone -- the button should already be disabled
      // whenever this isn't true, but the network boundary should
      // never rely on DOM state it doesn't control. Readiness now
      // requires every person to be explicitly CONFIRMED, not merely
      // field-complete -- matches renderSummary()'s identical gate.
      var progress = computeProgress();
      var allConfirmed = !!state.relationshipKey && progress.total > 0 && progress.confirmedCount === progress.total && !progress.anyBlocked;
      if (!allConfirmed) return;

      // A Customised person needs the Customisation product/variant to
      // still be resolvable at submit time -- catalog data (Gender/
      // Size) is re-checked above via computeProgress()/
      // personCompletionInfo(), but the Customisation product is a
      // separate, merchant-managed Theme Setting (assets/pt-
      // customisation-popup.js) that could in principle change between
      // page load and this click. Fail safely rather than submit a
      // line with no real variant id. Mirrors Fabric's identical
      // handleAddToCart() guard.
      var anyCustomisedPerson = state.people.some(function (p) { return isCustomised(p); });
      var custProductInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : { available: false };
      if (anyCustomisedPerson && (!custProductInfo || !custProductInfo.available)) {
        setCartError('The Customisation service is temporarily unavailable. Please remove Customisation from your matching set or try again shortly.');
        return;
      }

      var setId = editMode ? window.PTCartEdit.id : generateSetId();
      var relationship = state.relationshipKey;

      // Phase 11C.1 (corrected): reuse the existing personLabel()
      // helper (already used for the on-page person cards) so the
      // cart shows the same "Woman"/"Man"/"Kid 1" wording. No discount
      // tier is submitted here -- the Matching Benefit is a cart-wide
      // calculation across all qualifying merchandise (which may come
      // from other PDPs added separately), not something one builder
      // submission can know. That is Phase 11C.2's job. Unaffected by
      // Customisation: the ₹500 Customisation line is separate
      // merchandise with its own price, not a discount input.
      var items = [];
      state.people.forEach(function (person) {
        var garmentProperties = {
          Person: GENDER_TEXT[person.gender],
          _set_id: setId,
          _relationship: relationship,
          _person: person.id
        };

        garmentProperties.Style = window.PTStyleCatalog.cartValue(person.gender, person.style);
        garmentProperties._style_code = person.style;

        if (isCustomised(person)) {
          // A private id shared by this pair (garment + Customisation
          // line) purely for order-data correlation -- it is NOT what
          // makes the Customisation line unique in the cart (see
          // below); that is its own job. Never reuses _set_id/_person
          // as the Customisation identity, per the task instruction.
          var customisationId = person.cartCustomisationId || window.PTCustomisation.generateId();
          if (editMode) person.cartCustomisationId = customisationId;
          var draft = person.customisation;

          garmentProperties.Customisation = 'Yes — see the linked Customisation line';
          garmentProperties._customisation_id = customisationId;

          var custProperties = {
            Profile: draft.profileName || personLabel(person),
            Gender: GENDER_TEXT[person.gender],
            'Measurements Later': draft.measurementsLater ? 'Yes' : 'No',
            _customisation_id: customisationId
          };
          if (draft.topSize) custProperties['Top Size'] = draft.topSize;
          if (draft.bottomSize) custProperties['Bottom Size'] = draft.bottomSize;
          custProperties.Style = window.PTStyleCatalog.label(person.gender, person.style);
          custProperties._style_code = person.style;
          if (draft.chest) custProperties.Chest = draft.chest + ' in';
          if (draft.waist) custProperties.Waist = draft.waist + ' in';
          if (draft.hips) custProperties.Hips = draft.hips + ' in';
          if (draft.height) custProperties.Height = draft.height;
          if (draft.notes) custProperties.Notes = draft.notes;

          // Custom Size keeps measurements on the linked service line.
          items.push({
            id: variantIdForGender(person.gender),
            quantity: 1,
            properties: garmentProperties
          });
          items.push({
            id: custProductInfo.variantId,
            quantity: 1,
            properties: custProperties
          });
        } else {
          garmentProperties.Size = person.size;
          items.push({
            id: variantIdForGender(person.gender),
            quantity: 1,
            properties: garmentProperties
          });
        }
      });

      if (items.some(function (item) { return !item.id; })) {
        setCartError('Something went wrong preparing your matching set. Please refresh the page and try again.');
        return;
      }

      setCartError(null);
      state.submitting = true;
      render();

      var submission = editMode
        ? window.PTCartEdit.save(editSession, items)
        : fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          return response.json()
            .catch(function () { return null; })
            .then(function (json) { return { ok: response.ok, json: json }; });
        });
      submission
        .then(function (result) {
          if (!result.ok) {
            var errorMessage = 'We could not add your matching set to the cart. Please try again.';
            if (result.json) {
              if (typeof result.json.description === 'string' && result.json.description) {
                errorMessage = result.json.description;
              } else if (typeof result.json.message === 'string' && result.json.message) {
                errorMessage = result.json.message;
              }
            }
            throw new Error(errorMessage);
          }
          if (!result.json || !Array.isArray(result.json.items)) {
            throw new Error('We could not add your matching set to the cart. Please try again.');
          }

          state.submitting = false;
          render();

          // Native Symmetry 8.3.2 mechanism (confirmed during the
          // Phase 4 assessment): on:cart:change refreshes both the
          // header cart count (PageHeader.refreshHeader) and the
          // cart-drawer's own nested cart-form; dispatch:cart-drawer:
          // open then opens the drawer. No manual DOM/count updates,
          // no second refresh system, no dispatch:cart-drawer:refresh
          // alongside it (redundant once on:cart:change fires).
          document.dispatchEvent(new CustomEvent('on:cart:change', {
            bubbles: true,
            cancelable: false
          }));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
            bubbles: true,
            cancelable: false,
            detail: { opener: els.cta }
          }));
        })
        .catch(function (error) {
          if (editMode) editBlocked = true;
          state.submitting = false;
          setCartError(error && error.message ? error.message : 'We could not add your matching set to the cart. Please try again.');
          render();
          if (editMode) window.PTCartEdit.showRecovery(els.cartError);
          // eslint-disable-next-line no-console
          console.log(error);
        });
    }

    function onChange(event) {
      if (editMode && (editBlocked || state.submitting)) return;
      var target = event.target;
      if (!target || !target.matches) return;

      if (target.matches('[data-matching-relationship-input]')) {
        selectRelationship(target.value);
        return;
      }
      if (target.matches('[data-matching-field]')) {
        var field = target.getAttribute('data-matching-field');
        var personId = target.getAttribute('data-person-id');
        if (field === 'gender') requestGenderChange(personId, target.value);
        else if (field === 'size') setPersonSize(personId, target.value);
        else if (field === 'style') setPersonStyle(personId, target.value);
      }
    }

    function onClick(event) {
      if (editMode && (editBlocked || state.submitting)) return;
      var addBtn = event.target.closest && event.target.closest('[data-matching-add-person]');
      if (addBtn) {
        changeGroupCount(addBtn.getAttribute('data-matching-add-person'), 1);
        return;
      }

      var confirmBtn = event.target.closest && event.target.closest('[data-matching-person-confirm]');
      if (confirmBtn && !confirmBtn.disabled) {
        var confirmPersonEl = confirmBtn.closest('[data-person-id]');
        var confirmPersonId = confirmPersonEl ? confirmPersonEl.getAttribute('data-person-id') : null;
        if (confirmPersonId) confirmPerson(confirmPersonId);
        return;
      }

      var genderYesBtn = event.target.closest && event.target.closest('[data-matching-gender-confirm-yes]');
      if (genderYesBtn) {
        var yesPersonEl = genderYesBtn.closest('[data-person-id]');
        var yesPersonId = yesPersonEl ? yesPersonEl.getAttribute('data-person-id') : null;
        if (yesPersonId) confirmGenderChange(yesPersonId);
        return;
      }

      var genderNoBtn = event.target.closest && event.target.closest('[data-matching-gender-confirm-no]');
      if (genderNoBtn) {
        var noPersonEl = genderNoBtn.closest('[data-person-id]');
        var noPersonId = noPersonEl ? noPersonEl.getAttribute('data-person-id') : null;
        if (noPersonId) cancelGenderChange(noPersonId);
        return;
      }

      // Same button serves both "open Customisation" (not yet
      // Customised) and "Edit Customisation" (already Customised) --
      // both are a plain open() call with whatever draft already
      // exists (see draftForPopup()); only the popup's own explicit
      // "CUSTOMISE THIS SET" click (its onSave callback, wired here)
      // ever changes this person's state. See assets/pt-customisation-
      // popup.js for the exploration-only guarantee.
      //
      // Profile Name suggestion (2026-09-12 validation-loophole fix):
      // the person's own label ("Woman"/"Kid 1"/etc.) must remain
      // visible as a suggestion the first time the popup opens, but it
      // must NOT by itself satisfy the shared popup's own
      // hasMeaningfulDraftContent() gate -- a customer who opens the
      // popup and clicks "CUSTOMISE THIS SET" without touching
      // anything must still be blocked. The label is therefore no
      // longer written into initialDraft.profileName (which becomes
      // the field's real .value -- i.e. real draft content the shared
      // popup would treat as meaningful input). Instead, once the
      // popup has opened and rendered its fields, the label is set
      // directly as that field's native `placeholder` attribute. A
      // native placeholder is visually present whenever the field is
      // empty but never contributes to input.value, so an untouched
      // field still reads as "" to the popup's own currentDraft()/
      // hasMeaningfulDraftContent() -- achieving the fix with ZERO
      // changes to assets/pt-customisation-popup.js/.liquid/.css (the
      // shared reusability boundary stays exactly as it was; this
      // remains entirely a Matching-caller-side concern). A customer's
      // own previously-typed/saved profileName (already present on
      // `existingDraft`, copied into initialDraft below unchanged)
      // still becomes the field's real, editable value exactly as
      // before, and is still what gets saved -- this only changes what
      // happens when profileName is otherwise empty.
      var customiseBtn = event.target.closest && event.target.closest('[data-matching-customise-cta]');
      if (customiseBtn) {
        var customiseSetEl = customiseBtn.closest('[data-person-id]');
        var customisePersonId = customiseSetEl ? customiseSetEl.getAttribute('data-person-id') : null;
        var customisePerson = customisePersonId ? findPerson(customisePersonId) : null;
        if (customisePerson && customisePerson.gender && window.PTCustomisation) {
          var existingDraft = draftForPopup(customisePerson);
          // Shallow copy (not Object.assign, to stay ES5-consistent
          // with the rest of this file). No default is written into
          // initialDraft.profileName here any more -- see the file
          // comment above.
          var initialDraft = {};
          if (existingDraft) {
            for (var draftKey in existingDraft) {
              if (Object.prototype.hasOwnProperty.call(existingDraft, draftKey)) {
                initialDraft[draftKey] = existingDraft[draftKey];
              }
            }
          }
          window.PTCustomisation.open({
            gender: customisePerson.gender,
            initialDraft: initialDraft,
            triggerEl: customiseBtn,
            onSave: function (draft) { saveCustomisation(customisePersonId, draft); }
          });
          // Public data-* hook exposed by the shared popup's own
          // markup (snippets/pt-customisation-popup.liquid) -- reading/
          // setting a DOM attribute through a hook the popup already
          // exposes is not a change to the popup's files or its JS
          // module boundary. Harmless even if open() returned false
          // (product unavailable/gender not ready): the field simply
          // stays hidden in that case.
          var profileNameFieldEl = document.querySelector('[data-pt-customisation-field="profileName"]');
          if (profileNameFieldEl) profileNameFieldEl.placeholder = personLabel(customisePerson);
        }
        return;
      }

      var useStandardBtn = event.target.closest && event.target.closest('[data-matching-use-standard]');
      if (useStandardBtn) {
        var standardPersonEl = useStandardBtn.closest('[data-person-id]');
        var standardPersonId = standardPersonEl ? standardPersonEl.getAttribute('data-person-id') : null;
        if (standardPersonId) useStandardOptions(standardPersonId);
        return;
      }

      // [data-matching-person-edit-hint] sits alongside Remove (not
      // nested inside the header-toggle button -- browsers do not
      // allow nesting one button inside another) and routes through
      // the identical expand/re-collapse logic below via this shared
      // lookup, mirroring Fabric's identical
      // "[data-fabric-set-header], [data-fabric-set-edit-hint]" combined
      // selector exactly: tapping a collapsed card's header or its
      // "Edit"/"Continue" text expands it; tapping an already-expanded,
      // already-confirmed card's header re-collapses it without
      // requiring another tap of Confirm Person.
      var headerToggle = event.target.closest && event.target.closest('[data-matching-person-header], [data-matching-person-edit-hint]');
      if (headerToggle) {
        var headerPersonEl = headerToggle.closest('[data-person-id]');
        var headerPersonId = headerPersonEl ? headerPersonEl.getAttribute('data-person-id') : null;
        var headerPerson = headerPersonId ? findPerson(headerPersonId) : null;
        if (headerPerson) {
          if (!headerPerson.expanded) {
            expandPerson(headerPersonId);
            scrollPersonIntoView(headerPersonId);
          } else if (headerPerson.confirmed) {
            headerPerson.expanded = false;
            render();
          }
        }
        return;
      }

      var removeBtn = event.target.closest && event.target.closest('[data-matching-person-remove]');
      if (removeBtn && !removeBtn.hidden) {
        changeGroupCount(removeBtn.getAttribute('data-matching-remove-group'), -1);
        return;
      }
      var cartBtn = event.target.closest && event.target.closest('[data-matching-add-to-cart]');
      if (cartBtn && !cartBtn.disabled) {
        handleAddToCart();
      }
    }

    root.addEventListener('change', onChange);
    root.addEventListener('click', onClick);

    if (editMode) {
      render();
      setCartError('Loading your saved configuration…');
      if (!window.PTCartEdit || !window.PTCartEdit.id) {
        setCartError('This edit link cannot be loaded. Your cart is unchanged.');
        return;
      }
      window.PTCartEdit.load(data, 'matching', sizesForGender).then(function (session) {
        editSession = session;
        if (!RELATIONSHIP_DEFS[session.relationship]) throw new Error('The saved relationship is no longer supported. Your cart is unchanged.');
        selectRelationship(session.relationship, { silent: true });
        state.people = [];
        state.groups.forEach(function (group) {
          var saved = session.people.filter(function (person) {
            if (group.key === 'adult') return ['WOMEN', 'MEN'].indexOf(person.gender) !== -1;
            if (group.key === 'kid') return ['GIRLS', 'BOYS'].indexOf(person.gender) !== -1;
            return true;
          });
          if (saved.length > group.max) throw new Error('This group no longer matches the builder limits. Your cart is unchanged.');
          group.min = Math.min(group.min, saved.length);
          group.count = saved.length;
          if (group.labels) group.labels = saved.map(function (person) { return GENDER_TEXT[person.gender]; });
          saved.forEach(function (savedPerson, index) {
            var person = buildPerson(group, index);
            if (person.fixedGender) person.fixedGender = savedPerson.gender;
            Object.assign(person, savedPerson);
            state.people.push(person);
          });
        });
        state.people.sort(function (a, b) { return Number(a.id.split('-').pop()) - Number(b.id.split('-').pop()); });
        personCounter = Math.max(personCounter, ...session.people.map(function (person) { return Number(person.id.split('-').pop()); }));
        var radio = root.querySelector('[data-matching-relationship-input][value="' + session.relationship + '"]');
        if (radio) radio.checked = true;
        editBlocked = false;
        setCartError(null);
        window.PTCartEdit.showNotice(els.peopleContainer, session.people.some(function (person) { return !person.confirmed; })
          ? 'We restored your saved selections. Review the open cards, then select UPDATE CART to replace this set.' : null);
        render();
      }).catch(function (error) {
        setCartError(error.message || 'The configuration could not be loaded. Your cart is unchanged.');
        render();
        // Offer rebuilding only after cart ownership and saved details passed
        // the shared audit. Keep the same update target and original snapshot.
        window.PTCartEdit.showRecovery(els.cartError, editSession ? function () {
          state.relationshipKey = null;
          state.groups = [];
          state.people = [];
          root.querySelectorAll('[data-matching-relationship-input]').forEach(function (radio) { radio.checked = false; });
          if (RELATIONSHIP_DEFS[editSession.relationship]) {
            selectRelationship(editSession.relationship, { silent: true });
            var radio = root.querySelector('[data-matching-relationship-input][value="' + editSession.relationship + '"]');
            if (radio) radio.checked = true;
          }
          editBlocked = false;
          setCartError(null);
          window.PTCartEdit.showNotice(els.peopleContainer, 'Choose your configuration again. UPDATE CART will replace the existing set; your cart stays unchanged until you save.');
          render();
        } : null);
      });
      return;
    }

    // Relationship auto-init (Implementation Approval, 2026-09-07):
    // system-driven, NOT customer engagement -- see the `silent`
    // option on selectRelationship() above. Reuses that exact same
    // function and its render() path (no parallel renderer, no
    // duplicate person-card generation); the only addition is
    // checking the matching radio input so the tile visually reads
    // as selected exactly as it would after a genuine click, since
    // that visual state is driven purely by native `:checked` and
    // selectRelationship() itself never touches it. Falls through to
    // the existing unconditional render() whenever no relationship
    // could be resolved (missing/unrecognized tag) or the matching
    // radio can't be found -- today's blank-state behavior is
    // otherwise completely unchanged, and this never throws.
    var autoInitialized = false;
    var detectedRelationshipKey = data.detectedRelationship;
    if (detectedRelationshipKey && RELATIONSHIP_DEFS[detectedRelationshipKey]) {
      var initialRelationshipInput = root.querySelector(
        '[data-matching-relationship-input][value="' + detectedRelationshipKey + '"]'
      );
      if (initialRelationshipInput) {
        initialRelationshipInput.checked = true;
        selectRelationship(detectedRelationshipKey, { silent: true });
        autoInitialized = true;
      }
    }

    if (!autoInitialized) {
      render();
    }
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-matching-builder]');
    for (var i = 0; i < roots.length; i++) {
      init(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor re-renders a section's DOM on save without a full
  // page reload; re-run init so a freshly-rendered instance gets
  // bound. init() itself is idempotent (data-matching-initialized
  // guard) so this never double-attaches listeners on a node that
  // survives a reload unchanged.
  document.addEventListener('shopify:section:load', initAll);
})();
